import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { clearPersistedOrganizationScopeForUser } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSwitcher } from "../components/ui/LanguageSwitcher";
import { AlertCircle, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";
import LoadingScreen from "../components/LoadingScreen";

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSplash, setShowSplash] = useState(true);
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const userData = await login(email, password);
      clearPersistedOrganizationScopeForUser(userData.id);
      toast.success(t.auth.welcomeBack + "!");
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // Get localized loading text
  const loadingText = t.auth?.loadingText || (language === 'cs' ? 'Načítání Fleetora' : 'Initializing Fleetora');

  // Localized content
  const content = {
    tagline: language === 'cs' ? 'PROVOZ DRONŮ & COMPLIANCE. AUTOMATIZOVÁNO.' : 'DRONE OPS & COMPLIANCE. AUTOMATED.',
    subtitle: language === 'cs' ? 'Provozní platforma pro UAS operátory' : 'Operational platform for UAS operators',
    features: [
      language === 'cs' ? 'SORA 2.5 hodnocení rizik' : 'SORA 2.5 risk assessment',
      language === 'cs' ? 'CheckMate AI autorizace' : 'CheckMate AI authorization',
      language === 'cs' ? 'Správa flotily & certifikací' : 'Fleet & certification management',
    ],
  };

  return (
    <>
      {/* Branded Loading Screen */}
      {showSplash && (
        <LoadingScreen 
          onComplete={() => setShowSplash(false)}
          loadingText={loadingText}
          minDisplayTime={1000}
          language={language}
        />
      )}

      <div className="min-h-screen flex" data-testid="login-page">
        {/* Left Side - Hero Section */}
        <div 
          className="hidden xl:flex xl:w-[60%] lg:flex lg:w-[50%] relative"
          style={{ overflow: 'hidden' }}
        >
          {/* Hero image with object-fit for precise positioning */}
          <img
            src={`${process.env.PUBLIC_URL}/brand/fleetora-hero-drone.jpg`}
            alt=""
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: '65% center'
            }}
          />
          
          {/* Dark overlay */}
          <div 
            className="absolute inset-0" 
            style={{ background: 'rgba(10, 20, 35, 0.72)' }} 
          />
          
          {/* Aeroway logo - top left */}
          <div style={{ position: 'absolute', top: '24px', left: '32px', zIndex: 20 }}>
            <img
              src={`${process.env.PUBLIC_URL}/brand/aeroway-logo.png`}
              alt="Aeroway"
              style={{ height: '160px', filter: 'brightness(0) invert(1)', opacity: 0.8 }}
            />
          </div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col justify-between p-12 w-full">
            {/* Top - Empty for balance */}
            <div />
            
            {/* Center - Main content */}
            <div className="max-w-xl">
              <h1 
                className="text-white font-bold mb-4"
                style={{ 
                  fontSize: '60px', 
                  letterSpacing: '-0.02em',
                  lineHeight: '1.1'
                }}
              >
                FLEETORA
              </h1>
              <p 
                className="uppercase tracking-widest"
                style={{ 
                  fontSize: '16px', 
                  color: 'rgba(255, 255, 255, 0.8)',
                  letterSpacing: '0.15em',
                  marginBottom: '16px'
                }}
              >
                {content.tagline}
              </p>
              
              {/* Divider */}
              <hr style={{
                border: 'none',
                borderTop: '1px solid rgba(255,255,255,0.2)',
                margin: '16px 0'
              }} />
              
              {/* Descriptive paragraph */}
              <p style={{
                fontSize: '15px',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: '1.6',
                maxWidth: '420px',
                margin: '0 0 16px 0'
              }}>
                {language === 'cs'
                  ? 'Komplexní platforma pro správu dronové flotily, SORA 2.5 hodnocení rizik a přípravu dokumentace pro autorizaci ÚCL.'
                  : 'Complete platform for drone fleet management, SORA 2.5 risk assessment and documentation for CAA authorization.'
                }
              </p>
              
              {/* Feature bullets */}
              <div className="space-y-4">
                {content.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <Check className="h-4 w-4 flex-shrink-0" style={{ color: '#4ADE80' }} />
                    <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Bottom spacer for balance */}
            <div />
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div 
          className="flex-1 xl:w-[40%] lg:w-[50%] flex flex-col items-center justify-center relative min-h-screen"
          style={{ background: '#0F1824' }}
        >
          {/* Mobile/Tablet background image (below lg breakpoint) */}
          <div 
            className="lg:hidden absolute inset-0"
            style={{
              backgroundImage: `url('${process.env.PUBLIC_URL}/brand/fleetora-hero-drone.jpg')`,
              backgroundSize: "cover",
              backgroundPosition: "center center"
            }}
          >
            <div className="absolute inset-0" style={{ background: 'rgba(10, 20, 35, 0.88)' }} />
          </div>
          
          {/* Top bar with language switcher */}
          <div className="absolute top-0 right-0 z-10 p-4">
            <LanguageSwitcher />
          </div>
          
          <div className="relative z-10 w-full max-w-sm px-8">
              {/* Logo (contains FLEETORA text) + subtitle */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' }}>
                <img 
                  src={`${process.env.PUBLIC_URL}/brand/fleetora-logo-circle-2.png`}
                  alt="Fleetora" 
                  style={{ width: '110px', height: '110px' }}
                />
                <p style={{ fontSize: '13px', color: '#8A9AA8', margin: '8px 0 0' }}>
                  {content.subtitle}
                </p>
              </div>

              {/* Divider */}
              <div className="w-full h-px mb-8" style={{ background: 'rgba(255, 255, 255, 0.1)' }} />

              {error && (
                <div 
                  className="mb-6 flex items-center gap-2 rounded-lg px-4 py-3 text-sm"
                  style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#F87171' }}
                  data-testid="login-error"
                >
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm" style={{ color: '#8A9AA8' }}>
                    {t.auth.email}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                    className="h-12 border-0 text-white placeholder:text-slate-500"
                    style={{ 
                      background: '#1A2332',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'rgba(255, 255, 255, 0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4ECDC4'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm" style={{ color: '#8A9AA8' }}>
                    {t.auth.password}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="password-input"
                      className="h-12 border-0 text-white placeholder:text-slate-500 pr-10"
                      style={{ 
                        background: '#1A2332',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#4ECDC4'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: '#8A9AA8' }}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 text-white font-medium transition-all"
                  style={{ 
                    background: '#1E3A5F',
                    marginTop: '24px'
                  }}
                  disabled={loading}
                  data-testid="login-submit-btn"
                  onMouseEnter={(e) => e.target.style.background = '#2A4A72'}
                  onMouseLeave={(e) => e.target.style.background = '#1E3A5F'}
                >
                  {loading ? "..." : t.auth.signIn}
                </Button>
              </form>

              {/* Footer - inside form area */}
              <div className="text-center mt-8">
                <p style={{ fontSize: '11px', color: '#6B7A88' }}>
                  © 2026 Fleetora by Aeroway
                </p>
              </div>
          </div>
        </div>
      </div>
    </>
  );
};
